import React, { useState, useEffect, useContext, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import AuthContext from '../contexts/AuthContext';

const AdminUserInfo = () => {
  const { user, loading: authLoading } = useContext(AuthContext);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [userAnalytics, setUserAnalytics] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'ascending' });
  const navigate = useNavigate();

  useEffect(() => {
    if (authLoading) return;
    
    if (!user) {
      navigate('/login');
      return;
    }
    
    if (!user.isAdmin) {
      navigate('/test-list');
      return;
    }
    
    fetchUserAnalytics();
  }, [user, authLoading]);

  const fetchUserAnalytics = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await api.get('/admin/user-info');
      setUserAnalytics(response.data);
    } catch (err) {
      console.error('Error fetching user analytics:', err);
      setError('Failed to load user analytics');
    } finally {
      setLoading(false);
    }
  };

  const filteredAndSortedUsers = useMemo(() => {
    let filtered = userAnalytics.filter(user =>
      user.fullName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.email?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      user.phone?.toLowerCase().includes(searchTerm.toLowerCase())
    );

    if (sortConfig.key) {
      filtered.sort((a, b) => {
        let aVal = a[sortConfig.key];
        let bVal = b[sortConfig.key];

        if (sortConfig.key === 'registrationDate') {
          aVal = new Date(a.registrationDate);
          bVal = new Date(b.registrationDate);
        } else if (typeof aVal === 'string') {
          aVal = parseFloat(aVal) || 0;
          bVal = parseFloat(bVal) || 0;
        }

        if (aVal < bVal) {
          return sortConfig.direction === 'ascending' ? -1 : 1;
        }
        if (aVal > bVal) {
          return sortConfig.direction === 'ascending' ? 1 : -1;
        }
        return 0;
      });
    }

    return filtered;
  }, [userAnalytics, searchTerm, sortConfig]);

  const handleSort = (key) => {
    let direction = 'ascending';
    if (sortConfig.key === key && sortConfig.direction === 'ascending') {
      direction = 'descending';
    }
    setSortConfig({ key, direction });
  };

  const handleInDepthAnalysis = (userId) => {
    // For now, navigate to a detail page or show modal
    // Placeholder: alert or console
    console.log(`In-depth analysis for user ${userId}`);
    // Could navigate to `/admin/user/${userId}`
    // navigate(`/admin/user/${userId}`);
  };

  const getAccuracyColor = (accuracy) => {
    if (accuracy >= 90) return '#28a745';
    if (accuracy >= 70) return '#ffc107';
    return '#dc3545';
  };

  if (authLoading || loading) {
    return (
      <div style={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        minHeight: '50vh',
        flexDirection: 'column'
      }}>
        <div style={{
          border: '4px solid #f3f3f3',
          borderTop: '4px solid #3498db',
          borderRadius: '50%',
          width: '50px',
          height: '50px',
          animation: 'spin 1s linear infinite'
        }}></div>
        <p style={{ marginTop: '1rem', color: '#6c757d' }}>Loading user info...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div style={{
        textAlign: 'center',
        padding: '3rem',
        background: '#fff5f5',
        border: '1px solid #fed7d7',
        borderRadius: '12px',
        color: '#e53e3e',
        margin: '2rem'
      }}>
        <h3 style={{ margin: '0 0 1rem 0' }}>Error Loading User Info</h3>
        <p>{error}</p>
        <button
          onClick={fetchUserAnalytics}
          style={{
            marginTop: '1rem',
            padding: '0.75rem 1.5rem',
            backgroundColor: '#3498db',
            color: 'white',
            border: 'none',
            borderRadius: '6px',
            cursor: 'pointer'
          }}
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div style={{
      padding: '2rem',
      maxWidth: '1400px',
      margin: '0 auto',
      backgroundColor: '#f8f9fa',
      minHeight: '100vh'
    }}>
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: '2rem',
        flexWrap: 'wrap',
        gap: '1rem'
      }}>
        <h1 style={{
          fontSize: '2rem',
          color: '#2c3e50',
          margin: 0
        }}>User Information & Performance</h1>
        <div style={{ display: 'flex', gap: '1rem', alignItems: 'center' }}>
          <input
            type="text"
            placeholder="Search users..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              padding: '0.5rem 1rem',
              borderRadius: '6px',
              border: '1px solid #e9ecef',
              fontSize: '0.875rem',
              width: '250px'
            }}
          />
          <button
            onClick={fetchUserAnalytics}
            style={{
              padding: '0.5rem 1rem',
              backgroundColor: '#3498db',
              color: 'white',
              border: 'none',
              borderRadius: '6px',
              fontSize: '0.875rem',
              cursor: 'pointer',
              transition: 'background-color 0.2s ease'
            }}
          >
            Refresh
          </button>
        </div>
      </div>

      {/* Main User Analytics Table */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginBottom: '2rem',
        overflowX: 'auto'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          color: '#2c3e50',
          margin: '0 0 1rem 0'
        }}>User Performance Overview</h2>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '800px'
        }}>
          <thead>
            <tr>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('fullName')}
              >
                Name {sortConfig.key === 'fullName' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('email')}
              >
                Email {sortConfig.key === 'email' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('phone')}
              >
                Phone {sortConfig.key === 'phone' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('totalAttempts')}
              >
                Total Attempts {sortConfig.key === 'totalAttempts' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('uniqueTests')}
              >
                Unique Tests {sortConfig.key === 'uniqueTests' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('avgWpm')}
              >
                Avg WPM {sortConfig.key === 'avgWpm' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('avgAccuracy')}
              >
                Avg Accuracy {sortConfig.key === 'avgAccuracy' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('avgDictationWpm')}
              >
                Avg Dictation WPM {sortConfig.key === 'avgDictationWpm' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa',
                cursor: 'pointer'
              }}
                onClick={() => handleSort('registrationDate')}
              >
                Registration Date {sortConfig.key === 'registrationDate' && (sortConfig.direction === 'ascending' ? '▲' : '▼')}
              </th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredAndSortedUsers.map((userInfo, index) => (
              <tr key={userInfo.id}>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef' }}>
                  {userInfo.fullName || 'Unknown'}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef' }}>
                  {userInfo.email || 'N/A'}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef' }}>
                  {userInfo.phone || 'N/A'}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  {userInfo.totalAttempts}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  {userInfo.uniqueTests}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  {userInfo.avgWpm}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center', color: getAccuracyColor(userInfo.avgAccuracy) }}>
                  {userInfo.avgAccuracy}%
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  {userInfo.avgDictationWpm}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  {new Date(userInfo.registrationDate).toLocaleDateString()}
                </td>
                <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                  <button
                    onClick={() => handleInDepthAnalysis(userInfo.id)}
                    style={{
                      padding: '0.25rem 0.75rem',
                      backgroundColor: '#3498db',
                      color: 'white',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer',
                      fontSize: '0.75rem'
                    }}
                  >
                    In-Depth Analysis
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Summary Table - e.g., Top Performers */}
      <div style={{
        background: 'white',
        padding: '1.5rem',
        borderRadius: '12px',
        border: '2px solid #e9ecef',
        boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
        marginBottom: '2rem',
        overflowX: 'auto'
      }}>
        <h2 style={{
          fontSize: '1.25rem',
          color: '#2c3e50',
          margin: '0 0 1rem 0'
        }}>Top 10 Users by Average Accuracy</h2>
        <table style={{
          width: '100%',
          borderCollapse: 'collapse',
          minWidth: '600px'
        }}>
          <thead>
            <tr>
              <th style={{
                padding: '0.75rem',
                textAlign: 'left',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>Name</th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>Avg Accuracy</th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>Total Attempts</th>
              <th style={{
                padding: '0.75rem',
                textAlign: 'center',
                borderBottom: '2px solid #e9ecef',
                fontSize: '0.875rem',
                color: '#6c757d',
                backgroundColor: '#f8f9fa'
              }}>Avg WPM</th>
            </tr>
          </thead>
          <tbody>
            {[...userAnalytics]
              .sort((a, b) => b.avgAccuracy - a.avgAccuracy)
              .slice(0, 10)
              .map((userInfo, index) => (
                <tr key={userInfo.id}>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef' }}>
                    {userInfo.fullName || 'Unknown'}
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center', color: getAccuracyColor(userInfo.avgAccuracy) }}>
                    {userInfo.avgAccuracy}%
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                    {userInfo.totalAttempts}
                  </td>
                  <td style={{ padding: '0.75rem', borderBottom: '1px solid #e9ecef', textAlign: 'center' }}>
                    {userInfo.avgWpm}
                  </td>
                </tr>
              ))}
          </tbody>
        </table>
      </div>

      {/* Add CSS animation */}
      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default AdminUserInfo;
